import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { ao } from 'three/addons/tsl/display/GTAONode.js';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { outline } from 'three/addons/tsl/display/OutlineNode.js';
import type OutlineNode from 'three/addons/tsl/display/OutlineNode.js';
import { smaa } from 'three/addons/tsl/display/SMAANode.js';
import { color, float, mrt, normalView, output, pass, screenUV } from 'three/tsl';
import { RenderPipeline, type Node, type WebGPURenderer } from 'three/webgpu';
import { useSelectionStore } from '@/state/selection';
import { useTourStore } from '@/state/tour';
import { vignette } from './nodes';

const VISIBLE_EDGE = color(0x4cc9f0);
const HIDDEN_EDGE = color(0x1b6f8a);

// Scene pass with normals in a second target, ambient occlusion from depth and normals, SMAA,
// the hover outline (through walls too), bloom on the emissive states, a vignette. Tone mapping
// and the colour space conversion happen in the pipeline's output step.
export function Post() {
  const get = useThree((s) => s.get);
  const reduced = useTourStore((s) => s.reducedMotion);
  const hoverMeshes = useSelectionStore((s) => s.hoverMeshes);
  const stack = useRef<{ pipeline: RenderPipeline; outlinePass: OutlineNode } | null>(null);

  useEffect(() => {
    const { gl, scene, camera } = get();
    const scenePass = pass(scene, camera);
    scenePass.setMRT(mrt({ output, normal: normalView }));
    const colorNode = scenePass.getTextureNode('output');
    const depthNode = scenePass.getTextureNode('depth');
    const normalNode = scenePass.getTextureNode('normal');

    let image: Node<'vec4'> = colorNode;
    if (!reduced) {
      const aoPass = ao(depthNode, normalNode, camera);
      aoPass.resolutionScale = 0.5;
      image = image.mul(aoPass.getTextureNode().r);
    }
    image = smaa(image) as unknown as Node<'vec4'>;

    const outlinePass = outline(scene, camera, { edgeThickness: float(1.5), downSampleRatio: 2 });
    const edges = outlinePass.visibleEdge
      .mul(VISIBLE_EDGE)
      .add(outlinePass.hiddenEdge.mul(HIDDEN_EDGE))
      .mul(4);
    image = image.add(edges);

    const bloomPass = bloom(image, reduced ? 0.15 : 0.4, 0.5, 0.82);
    image = image.add(bloomPass);

    const pipeline = new RenderPipeline(gl as unknown as WebGPURenderer);
    pipeline.outputNode = vignette(image, screenUV, 0.25, 0.7);
    stack.current = { pipeline, outlinePass };
    return () => {
      pipeline.dispose();
      stack.current = null;
    };
  }, [get, reduced]);

  useEffect(() => {
    if (stack.current) stack.current.outlinePass.selectedObjects = hoverMeshes;
  }, [hoverMeshes]);

  // A priority above zero takes the frame over from r3f's own render call.
  useFrame(() => stack.current?.pipeline.render(), 1);
  return null;
}
